import { cn } from '../../lib/utils'

type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

interface LoadingSpinnerProps {
  size?: SpinnerSize
  className?: string
  message?: string
}

const sizeStyles: Record<SpinnerSize, string> = {
  xs: 'h-3 w-3',
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
  xl: 'h-12 w-12',
}

export function LoadingSpinner({ size = 'md', className, message }: LoadingSpinnerProps) {
  const spinner = (
    <svg
      className={cn('animate-spin text-primary', sizeStyles[size], className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-label="Loading"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )

  if (message) {
    return (
      <div className="flex flex-col items-center gap-3">
        {spinner}
        <p className="text-sm text-text-muted">{message}</p>
      </div>
    )
  }

  return spinner
}

interface LoadingOverlayProps {
  loading: boolean
  children: React.ReactNode
  message?: string
  className?: string
}

export function LoadingOverlay({ loading, children, message, className }: LoadingOverlayProps) {
  return (
    <div className={cn('relative', className)}>
      {children}
      {loading && (
        <div className="absolute inset-0 bg-background/70 flex items-center justify-center z-10">
          <LoadingSpinner size="lg" message={message} />
        </div>
      )}
    </div>
  )
}

interface FullScreenLoadingProps {
  message?: string
}

export function FullScreenLoading({ message = 'Loading...' }: FullScreenLoadingProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-8 flex flex-col items-center gap-4 shadow-xl">
        <LoadingSpinner size="xl" />
        <p className="text-lg font-medium text-text">{message}</p>
      </div>
    </div>
  )
}

export default LoadingSpinner
